import React from 'react';
import { useTranslation } from 'react-i18next';
import { BsClock, BsTags } from 'react-icons/bs';
import { GoPlus } from 'react-icons/go';
import { RiEdit2Line } from 'react-icons/ri';

import { useBookingStore } from '@/store/bookingStore';

interface BookingDetailsProps {
  onAddDetails?: (index: number) => void;
  className?: string;
}

const BookingDetails: React.FC<BookingDetailsProps> = ({
  onAddDetails,
  className = '',
}) => {
  const { customers, availableTreatments } = useBookingStore();
  const { t } = useTranslation();

  return (
    <div
      className={`rounded-lg border border-slate-200 bg-white p-4 ${className}`.trim()}
    >
      <div className="mb-2 flex h-6 items-center justify-between">
        <h2 className="text-primary-text leading-7 font-semibold">
          {t('payment.bookingDetails')}
        </h2>
      </div>

      <div className="space-y-2">
        {customers.map((customer, index) => {
          // Check if customer has any filled information to determine button text
          const hasCustomerInfo =
            customer.name ||
            customer.email ||
            customer.tel ||
            customer.remark ||
            (customer.therapistPreference?.preferredTherapistIds &&
              customer.therapistPreference.preferredTherapistIds.length > 0);

          return (
            <div key={index}>
              <div className="space-y-2">
                <div className="flex h-6 items-center justify-between">
                  <h3 className="text-secondary-text leading-5 font-semibold tracking-[0.08px]">
                    {customer.name || `${t('payment.customer')} ${index + 1}`}
                  </h3>
                  {onAddDetails && (
                    <button
                      type="button"
                      className="flex h-6 cursor-pointer items-center gap-1.5 rounded px-0 py-0.5"
                      onClick={() => onAddDetails(index)}
                    >
                      {hasCustomerInfo ? (
                        <>
                          <RiEdit2Line className="text-primary size-4" />
                          <span className="text-primary leading-6 font-semibold">
                            {t('components.bookingDetails.editDetails')}
                          </span>
                        </>
                      ) : (
                        <>
                          <GoPlus className="text-primary size-4" />
                          <span className="text-primary leading-6 font-semibold">
                            {t('payment.addDetails')}
                          </span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                {customer.treatmentItems.map((treatment, treatmentIndex) => {
                  // Find treatment details from available treatments
                  const treatmentDetails = availableTreatments.find(
                    (t) => t.treatmentId === treatment.treatmentId,
                  );
                  const price =
                    treatment.retailPrice > 0
                      ? treatment.retailPrice
                      : treatment.originalPrice;

                  return (
                    <div key={treatmentIndex} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <span className="text-primary-text leading-5 tracking-[0.08px]">
                            {treatmentDetails?.name ??
                              t('components.bookingDetails.treatmentFallback', {
                                id: treatment.treatmentId,
                              })}
                          </span>
                        </div>
                        <span className="text-secondary-text leading-6">
                          {t('components.bookingDetails.quantity', {
                            count: 1,
                          })}
                        </span>
                      </div>

                      <div className="flex items-center gap-[11px]">
                        <div className="flex items-center gap-1">
                          <BsClock className="text-text-muted size-3.5" />
                          <span className="text-secondary-text text-sm leading-5 tracking-[0.08px]">
                            {treatment.optionId}{' '}
                            {t('components.bookingDetails.minutesShort')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <BsTags className="text-text-muted size-4" />
                          <span className="text-secondary-text text-sm leading-5 tracking-[0.08px]">
                            {t('common.currency')}
                            {price.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Render customer remark if present */}
                {customer.remark && customer.remark.trim() !== '' && (
                  <span className="text-error">{customer.remark}</span>
                )}
              </div>

              {/* Divider between customers */}
              {index < customers.length - 1 && (
                <div className="mt-2 h-0 w-full">
                  <div className="border-t border-gray-200"></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BookingDetails;
